import { Box, Chip, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Student } from "../../types/students";
import { User } from "../../types/users";

interface Props {
  student: Student;
  users: User[];
  onDragStart: (id: string) => void;
  onClick: (student: Student) => void;
}

function KanbanCard({ student, users, onDragStart, onClick }: Props) {
  const navigate = useNavigate();
  const docs = student.additionalData?.documents ?? [];
  const totalSlots = docs.length * 2;
  const filledSlots = docs.reduce(
    (acc: number, doc: any) =>
      acc +
      (doc.parts?.arabic?.available ? 1 : 0) +
      (doc.parts?.english?.available ? 1 : 0),
    0
  );
  const docPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;

  const employee = users?.find((u) => u._id === student.employee);
  const initials = employee?.username?.slice(0, 2).toUpperCase() ?? "–";

  return (
    <Box
      draggable
      onDragStart={() => onDragStart(student._id)}
      onClick={() => onClick(student)}
      sx={{
        bgcolor: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "6px",
        p: 1.5,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.08)", borderColor: "#CBD5E1" },
        transition: "box-shadow 0.1s ease, border-color 0.1s ease",
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={0.5}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}
          >
            {student.fullName}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.25} flexShrink={0}>
            <Tooltip title="View profile" placement="top">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/students/${student._id}`); }}
                sx={{ width: 20, height: 20, color: "#94A3B8", "&:hover": { color: "#0EA5E9" } }}
              >
                <OpenInNew sx={{ fontSize: "0.75rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={employee?.username ?? "Unassigned"} placement="top">
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "#0F172A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "white", fontSize: "0.55rem", fontWeight: 700 }}>
                  {initials}
                </Typography>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "0.65rem" }}>
          {student.refNo}
        </Typography>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
          <Chip
            label={student.nationality}
            size="small"
            sx={{ fontSize: "0.6rem", height: 18, borderRadius: "3px" }}
          />
          {(student as any).commission > 0 && (
            <Chip
              label={`RM ${Number((student as any).commission).toLocaleString()}`}
              size="small"
              color="success"
              sx={{ fontSize: "0.6rem", height: 18, borderRadius: "3px" }}
            />
          )}
        </Stack>

        {totalSlots > 0 && (
          <Tooltip title={`Documents: ${docPct}% complete (${filledSlots}/${totalSlots})`} placement="bottom">
            <Box>
              <LinearProgress
                variant="determinate"
                value={docPct}
                sx={{
                  height: 3,
                  borderRadius: 999,
                  bgcolor: "#E2E8F0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: docPct === 100 ? "#059669" : docPct >= 50 ? "#0EA5E9" : "#EAB308",
                    borderRadius: 999,
                  },
                }}
              />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

export default KanbanCard;
